<template>
  <DataTable
    :value="courses"
    class="p-datatable-sm"
    stripedRows
    responsiveLayout="scroll"
    data-testid="registered-courses-table"
  >
    <Column field="course.course_code" class="w-40">
      <template #header>
        <span class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <i class="pi pi-hashtag text-[11px]"></i> Code
        </span>
      </template>
      <template #body="{ data }">
        <span class="inline-flex items-center gap-2">
          <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <i class="pi pi-book text-xs"></i>
          </span>
          <span class="font-semibold text-gray-800">{{ data.course?.course_code || '—' }}</span>
        </span>
      </template>
    </Column>

    <Column field="course.course_title">
      <template #header>
        <span class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <i class="pi pi-align-left text-[11px]"></i> Course Title
        </span>
      </template>
      <template #body="{ data }">
        <span class="text-gray-700">{{ data.course?.course_title || '—' }}</span>
      </template>
    </Column>

    <Column v-if="courses.some(c => c.module_name)" field="module_name" header="Module" />

    <Column field="course_status" class="w-40">
      <template #header>
        <span class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <i class="pi pi-tag text-[11px]"></i> Type
        </span>
      </template>
      <template #body="{ data }">
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
          :class="data.course_status === 'C' ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-600'"
        >
          <i class="text-[10px]" :class="data.course_status === 'C' ? 'pi pi-lock' : 'pi pi-sliders-h'"></i>
          {{ data.course_status === 'C' ? 'Compulsory' : 'Elective' }}
        </span>
      </template>
    </Column>

    <Column field="course.unit" class="w-28">
      <template #header>
        <span class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
          <i class="pi pi-chart-bar text-[11px]"></i> Units
        </span>
      </template>
      <template #body="{ data }">
        <span
          class="inline-flex min-w-9 items-center justify-center rounded-md border border-gray-200
            bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-700"
        >
          {{ data.course?.unit ?? 0 }}
        </span>
      </template>
    </Column>

    <template #empty>
      <div class="py-12 text-center">
        <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
          <i class="pi pi-inbox text-xl text-gray-400"></i>
        </span>
        <p class="mt-3 text-sm font-medium text-gray-600">Nothing registered yet</p>
        <p class="mt-1 text-xs text-gray-400">Courses you submit will be listed here.</p>
      </div>
    </template>

    <template #footer>
      <div class="flex items-center justify-end gap-2 text-sm">
        <i class="pi pi-calculator text-xs text-gray-400"></i>
        <span class="text-gray-500">Total Units</span>
        <span class="rounded-md bg-primary/10 px-2.5 py-1 font-bold text-primary">{{ totalUnits }}</span>
      </div>
    </template>
  </DataTable>
</template>

<script setup lang="ts">
import type { CourseRegistered } from '@/types/composition-functions';

// Only the fields the table shows, so the guardian portal's narrower query fits too.
type RegisteredCourse = Pick<CourseRegistered, 'module_name' | 'course_status'> & {
  course?: Pick<NonNullable<CourseRegistered['course']>, 'course_code' | 'course_title' | 'unit'> | null;
};

const props = defineProps<{
  courses: RegisteredCourse[];
}>();

const totalUnits = computed(() =>
  props.courses.reduce((sum, item) => sum + (Number(item.course?.unit) || 0), 0),
);
</script>
